import { ReactNode, useEffect } from 'react';
import { AnalyticsThemeProvider } from '@repo/ui/theme';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

interface BasePopupProps {
  isOpen?: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  icon?: ReactNode;
  headerContent?: ReactNode;
  className?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  variant?: 'default' | 'analytics';
}

const ANIMATION_CONFIG = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
};

export function BasePopup({
  isOpen = true,
  onClose,
  children,
  title,
  icon,
  headerContent,
  className = 'max-w-5xl',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  variant = 'default',
}: BasePopupProps) {
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'popup-title' : undefined}
    >
      <motion.div
        {...ANIMATION_CONFIG}
        className={`${variant === 'analytics' ? 'border border-[var(--anl-border)] bg-[var(--anl-surface)]' : 'bg-white'} flex max-h-[90vh] w-full flex-col overflow-hidden rounded-lg shadow-xl ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {(title || headerContent || showCloseButton) && (
          <div
            className={`flex items-center justify-between p-4 ${variant === 'analytics' ? 'border-b border-[var(--anl-border)] bg-[var(--anl-surface)]' : 'border-b bg-gradient-to-r from-blue-700/90 to-purple-600/90'}`}
          >
            {(title || icon) && (
              <h2
                id="popup-title"
                className={`flex items-center font-semibold ${isMobile ? 'text-base' : 'text-xl'} ${variant === 'analytics' ? 'text-[var(--anl-text)]' : 'text-white'}`}
              >
                {icon && <span className="mr-2">{icon}</span>}
                {title}
              </h2>
            )}

            <div className="flex items-center gap-3">
              {headerContent}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className={`${variant === 'analytics' ? 'text-[var(--anl-text)] hover:text-[var(--anl-text-muted)]' : 'text-white hover:text-gray-200'} rounded transition-colors focus:outline-none`}
                  aria-label="Close popup"
                >
                  <X className="h-6 w-6" />
                </button>
              )}
            </div>
          </div>
        )}

        <div
          className={`flex-1 overflow-y-auto ${variant === 'analytics' ? 'bg-[var(--anl-surface)] text-[var(--anl-text)]' : ''}`}
        >
          {children}
        </div>
      </motion.div>
    </div>
  );

  if (variant === 'analytics') {
    return <AnalyticsThemeProvider>{content}</AnalyticsThemeProvider>;
  }

  return content;
}
